export default {
  Title: 'Client Metrics Dashboard',
  Subtitle: 'Financial forecast and actuals from the daily financial snapshot',
  VsPriorDay: 'vs. Prior day',
  VsPriorWeek: 'vs. Prior week',
  Daily: 'Daily',
  Weekly: 'Weekly',
  DatePickerLabel: 'Select snapshot date',
  GeneratedAt: 'Snapshot generated #{timestamp}',
  ComparisonDates: '#{currentDate} vs #{previousDate}',
  Previous: 'Prev: #{value}',
  Unavailable: '--',
  WeekOf: 'Week of #{date}',
  MarginPoints: '#{value} pts',
  Errors: {
    LoadError: 'Unable to load client metrics right now.',
  },
  EmptyStates: {
    MissingSnapshot: 'No financial snapshot is available for #{date}. It may not have been generated yet.',
    MissingComparisonSnapshot: 'No comparison snapshot is available for #{date}, so change indicators are hidden.',
    NoForecastWeeks: 'This snapshot contains no forecast weeks.',
    NoActualWeeks: 'This snapshot contains no completed actual weeks.',
  },
  ClientOverview: {
    SectionTitle: 'CLIENT OVERVIEW',
    ActiveClients: 'ACTIVE CLIENTS IN FORECAST',
    ActiveClientsCaption: 'Distinct clients with scheduled care in the 13-week forecast window',
    ScheduledClientsThisWeek: 'SCHEDULED CLIENTS THIS WEEK',
    ScheduledClientsThisWeekCaption: 'Clients with scheduled care in the current week',
    Caregivers: 'CAREGIVERS IN FORECAST',
    CaregiversCaption: 'Distinct caregivers assigned in the 13-week forecast window',
    MissingClientRates: 'VISITS MISSING CLIENT RATE',
    MissingClientRatesCaption: 'Scheduled visits without a billable client rate',
  },
  FinancialOverview: {
    SectionTitle: 'FINANCIAL OVERVIEW',
    ForecastTitle: '13-week forecast',
    ForecastSubtitle: '#{startDate} - #{endDate}',
    ActualTitle: 'Trailing 13 completed weeks',
    ActualSubtitle: 'Through week of #{date}',
    ScheduledHours: 'SCHEDULED HOURS',
    WeightedHours: 'HOLIDAY-WEIGHTED HOURS',
    ForecastRevenue: 'FORECAST REVENUE',
    ForecastLabor: 'FORECAST CAREGIVER LABOUR',
    ForecastGrossProfit: 'FORECAST GROSS PROFIT',
    ForecastGrossMargin: 'FORECAST GROSS MARGIN',
    ConfirmedHours: 'CONFIRMED HOURS',
    UnconfirmedHours: 'UNCONFIRMED HOURS',
    ActualRevenue: 'ACTUAL REVENUE',
    ActualLabor: 'ACTUAL CAREGIVER LABOUR',
    ActualGrossProfit: 'ACTUAL GROSS PROFIT',
    ActualGrossMargin: 'ACTUAL GROSS MARGIN',
  },
  Forecast: {
    SectionTitle: '13-WEEK FORECAST',
    FinancialTitle: 'Financial forecast by week',
    FinancialSubtitle: 'Weekly revenue, caregiver labour, and gross profit',
    CapacityTitle: 'Capacity forecast by week',
    CapacitySubtitle: 'Hours on the left axis; clients and caregivers on the right axis',
    Revenue: 'Revenue',
    Labor: 'Caregiver labour',
    GrossProfit: 'Gross profit',
    ScheduledHours: 'Scheduled hours',
    WeightedHours: 'Holiday-weighted hours',
    ScheduledClients: 'Scheduled clients',
    Caregivers: 'Caregivers',
  },
  Actuals: {
    SectionTitle: 'ACTUAL PERFORMANCE',
    HoursTitle: 'Scheduled vs. confirmed hours',
    HoursSubtitle: 'Trailing completed weeks',
    MoneyTitle: 'Scheduled vs. actual financials',
    MoneySubtitle: 'Trailing completed weeks',
    ScheduledHours: 'Scheduled hours',
    ConfirmedHours: 'Confirmed hours',
    ScheduledPayHours: 'Weighted scheduled hours',
    ConfirmedPayHours: 'Weighted confirmed hours',
    ScheduledRevenue: 'Scheduled revenue',
    ActualRevenue: 'Actual revenue',
    ScheduledLabor: 'Scheduled caregiver labour',
    ActualLabor: 'Actual caregiver labour',
    GrossProfit: 'Gross profit',
    HoursExplainer: 'Holiday-weighted hours count statutory-holiday shifts at 1.5x for pay and billing. Scheduled and confirmed hours count each hour once.',
  },
  Projections: {
    SectionTitle: 'PROJECTIONS',
    PanelTitle: 'Run-rate projections from this week',
    PanelSubtitle: 'This week (#{week}) scaled to a month and a year',
    Metric: 'Metric',
    ThisWeek: 'This week',
    Monthly: 'Monthly (×#{multiplier})',
    Yearly: 'Yearly (×#{multiplier})',
    Revenue: 'Client revenue',
    Labor: 'Caregiver labour',
    GrossProfit: 'Net revenue',
    ScheduledHours: 'Scheduled hours',
    GrossMargin: 'Blended margin',
    HolidayWarning: 'This week includes statutory-holiday-weighted shifts (billed and paid at 1.5×). ' +
      'Extrapolating it assumes a holiday in every week, so the monthly and yearly figures overstate. ' +
      'A week with unusual schedule gaps skews the projection the other way.',
    BelowAverageWarning: 'This week is running below the 13-week forecast average, so these projections likely understate a typical month or year.',
    AboveAverageWarning: 'This week is running above the 13-week forecast average, so these projections likely overstate a typical month or year.',
    Empty: 'This snapshot has no current forecast week to project from.',
  },
  WeeklyBreakdown: {
    SectionTitle: 'WEEKLY BREAKDOWN',
    ForecastTitle: 'Forecast weeks',
    ForecastSubtitle: 'Current week plus the next 12, earliest first',
    ActualTitle: 'Completed weeks',
    ActualSubtitle: 'Trailing 13 completed weeks, latest first',
    Week: 'Week',
    ScheduledClients: 'Clients',
    Caregivers: 'Caregivers',
    ScheduledHours: 'Sched. hours',
    WeightedHours: 'Weighted hours',
    ConfirmedHours: 'Conf. hours',
    Revenue: 'Revenue',
    ScheduledRevenue: 'Sched. revenue',
    ActualRevenue: 'Actual revenue',
    Labor: 'Labour',
    ActualLabor: 'Actual labour',
    GrossProfit: 'Gross profit',
    GrossMargin: 'Gross margin',
    ClientSlips: 'Client slips',
    CaregiverSlips: 'Caregiver slips',
    SlipCompletion: '#{complete} / #{total}',
  },
  MonthlySummary: {
    SectionTitle: 'MONTHLY SUMMARY',
    ForecastTitle: 'Forecast by calendar month',
    ForecastSubtitle: 'Forecast weeks rolled into calendar months, earliest first',
    ActualTitle: 'Actuals by calendar month',
    ActualSubtitle: 'Completed weeks rolled into calendar months, latest first',
    Month: 'Month',
    ClientRevenue: 'Client revenue',
    Labor: 'Caregiver labour',
    NetRevenue: 'Net revenue',
    ScheduledHours: 'Scheduled hours',
    ConfirmedHours: 'Confirmed hours',
    BlendedMargin: 'Blended margin',
    Clients: 'Clients',
    Caregivers: 'Caregivers',
    Partial: 'Partial · #{covered} of #{total} days',
    Note: 'Weeks that straddle a month boundary are split across months by day. ' +
      'Blended margin is recomputed from monthly totals, not averaged. ' +
      'Client and caregiver counts show the per-week range within the month. ' +
      'Partial edge months cover only part of the 13-week window.',
  },
  ClientMonthly: {
    SectionTitle: 'CLIENT MONTHLY FORECAST',
    Title: 'Client monthly forecast',
    Subtitle: 'Scheduled forecast hours for #{month} — not actual hours worked. ' +
      'Weighted hours and estimated revenue apply the backend\'s holiday and pricing rules.',
    Summary: {
      ActiveClients: 'ACTIVE CLIENTS',
      ClientsWithHours: 'CLIENTS WITH SCHEDULED HOURS',
      ScheduledHours: 'SCHEDULED HOURS',
      WeightedHours: 'WEIGHTED HOURS',
      EstimatedRevenue: 'ESTIMATED REVENUE',
      MissingRates: 'CLIENTS MISSING RATE',
      UnpricedHoursCaption: '#{hours} hrs unpriced',
      NoUnpricedHours: 'All scheduled hours priced',
    },
    Table: {
      Client: 'Client',
      HourlyRate: 'Hourly rate',
      ScheduledHours: 'Scheduled hours',
      WeightedHours: 'Weighted hours',
      EstimatedRevenue: 'Est. revenue',
      Status: 'Data quality',
      MissingRate: 'Missing rate',
      Unpriced: 'Unpriced',
      PartlyUnpriced: '#{hours} hrs unpriced',
      NoScheduledHours: 'No scheduled hours',
      Priced: 'Priced',
      TotalsRow: 'All clients',
      TotalsNote: 'Full snapshot',
      TotalsCaption: 'Totals reflect every client in the snapshot, not only the rows shown.',
      SearchPlaceholder: 'Search client or RN',
      ResultCount: 'Showing #{shown} of #{total}',
      RnTooltip: 'RN: #{rn}',
      SortActive: 'Sorted by #{column} #{direction}. Activate to change direction.',
      SortInactive: 'Activate to sort by #{column}.',
      SortAscending: 'ascending',
      SortDescending: 'descending',
    },
    LoadMore: 'Load more clients',
    Retry: 'Try again',
    Errors: {
      LoadError: 'Unable to load the client monthly forecast right now.',
    },
    EmptyStates: {
      NoClients: 'This monthly snapshot has no clients scheduled for #{month}.',
      MissingSnapshot: 'No per-client monthly snapshot is available for #{date}. ' +
        'Older dates may predate this feature, or the snapshot may not have been generated yet.',
    },
  },
  ExpensesCompletion: {
    SectionTitle: 'EXPENSES & SLIP COMPLETION',
    Subtitle: 'Trailing 13 completed weeks. Expenses are pass-through amounts and are excluded from gross profit.',
    ClientExpenses: 'CLIENT EXPENSES',
    CaregiverExpenses: 'CAREGIVER EXPENSES',
    CompleteClientSlips: 'COMPLETE CLIENT SLIPS',
    IncompleteClientSlips: 'INCOMPLETE CLIENT SLIPS',
    CompleteCaregiverSlips: 'COMPLETE CAREGIVER SLIPS',
    IncompleteCaregiverSlips: 'INCOMPLETE CAREGIVER SLIPS',
  },
  DataQuality: {
    SectionTitle: 'DATA QUALITY',
    WarningAlert: 'This snapshot has data-quality warnings. Revenue or labour cost may be understated.',
    InfoAlert: 'This snapshot is missing one or more historical tracker weeks, so actual totals may be incomplete.',
    MissingActualWeeks: 'Missing actual tracker weeks',
    MissingActualWeekItem: 'Week of #{date}: missing #{trackers}',
    TrackerNames: {
      CLIENT: 'client tracker',
      CAREGIVER: 'caregiver tracker',
    },
    MissingClientRates: 'Missing client rates',
    MissingCaregiverRates: 'Missing caregiver rates',
    RateIssueValue: '#{count} occurrences, #{hours} hrs affected',
    UnassignedEvents: 'Unassigned scheduled events',
    SkippedEvents: 'Skipped schedule events',
    SkippedEventReasons: 'Skipped-event reasons',
    SkippedEventReasonItem: '#{reason}: #{count}',
  },
};
